/**
 * Rounded duration buckets used to present remaining time to users.
 *
 * @remarks
 * Mirrors Android's and iOS's `TimeDuration` behaviour:
 * - Durations are rounded to the nearest minute using a 40s threshold.
 * - The minimum duration is always 1 minute.
 * - Durations over 24h wrap using modulo 24h.
 */
export type TimeDuration =
  | { kind: 'oneMinute' }
  | { kind: 'oneHour' }
  | { kind: 'minutes'; minutes: number }
  | { kind: 'hours'; hours: number }
  | { kind: 'hoursAndMinutes'; hours: number; minutes: number };

const ROUNDING_THRESHOLD_SECONDS = 40;
const SECONDS_IN_MINUTE = 60;
const SECONDS_IN_DAY = 24 * 60 * 60;

/**
 * Factory namespace for {@link TimeDuration}.
 */
export const TimeDuration = {
  fromSeconds(seconds: number): TimeDuration {
    const nonNegativeSeconds = Math.max(seconds, 0);
    const adjustedSeconds =
      nonNegativeSeconds > SECONDS_IN_DAY
        ? nonNegativeSeconds % SECONDS_IN_DAY
        : nonNegativeSeconds;
    const roundedMinutes = roundToMinutes(adjustedSeconds);
    const hours = Math.floor(roundedMinutes / 60);
    const minutes = roundedMinutes % 60;

    if (roundedMinutes === 1) {
      return { kind: 'oneMinute' };
    }

    if (roundedMinutes < 60) {
      return { kind: 'minutes', minutes: roundedMinutes };
    }

    if (roundedMinutes === 60) {
      return { kind: 'oneHour' };
    }

    if (minutes === 0) {
      return { kind: 'hours', hours };
    }

    return { kind: 'hoursAndMinutes', hours, minutes };
  },

  mapToHoursAndMinutesComponents(duration: TimeDuration): {
    hours: number;
    minutes: number;
  } {
    if (duration.kind === 'oneMinute') {
      return { hours: 0, minutes: 1 };
    }

    if (duration.kind === 'oneHour') {
      return { hours: 1, minutes: 0 };
    }

    if (duration.kind === 'minutes') {
      return { hours: 0, minutes: duration.minutes };
    }

    if (duration.kind === 'hours') {
      return { hours: duration.hours, minutes: 0 };
    }

    return { hours: duration.hours, minutes: duration.minutes };
  },
} as const;

const roundToMinutes = (seconds: number): number => {
  const minutes = Math.floor(seconds / SECONDS_IN_MINUTE);
  const remainingSeconds = seconds % SECONDS_IN_MINUTE;
  if (remainingSeconds >= ROUNDING_THRESHOLD_SECONDS) {
    return minutes + 1;
  }
  return Math.max(1, minutes);
};
